// use-exchange-code-for-session.ts — Auth suite, action
//
// Exchanges an OAuth `code` query param for a Supabase session on the callback page.
// Sets the resolved session on local auth state. Components mount this in a one-shot
// useEffect on the auth callback page.

'use client';

import { useMemo } from 'react';
import type { User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { setAuthSession } from '@/lib/authState';
import { useActionHook } from '@/lib/query/use-action-hook';
import type { ActionResult } from '@/types/async-protocol';

export function useExchangeCodeForSession(): ActionResult<{ code: string }, User | null> {
  return useActionHook<{ code: string }, User | null>(
    useMemo(
      () => ({
        mutationFn: async ({ code }) => {
          const { data, error } = await supabase.auth.exchangeCodeForSession(code);
          if (error) throw error;
          if (data?.session) {
            setAuthSession(data.session);
          }
          return data?.user ?? null;
        },
      }),
      [],
    ),
  );
}
